import React, { memo, useCallback, useEffect, useState } from "react";
import { useTranslate } from "helpers";
import { Button } from "ui/components/Button/Button";
import { OfficeEditFormValues, OfficeAddressRow } from "services/api/offices/types";
import { getOfficeAddressesAPI, deleteOfficeAddressAPI } from "services/api/offices/api";
import { initialDataWithPagination } from "services/api/consts";
import { Dialog } from "ui/components/Dialog";
import { TableSearch } from "ui/components/Table/TableSearch";
import OfficeAddressEditCard from "./OfficeAddressEditCard/OfficeAddressEditCard";
import { OFFICE_ADDRESS_COLUMNS } from "./consts";

interface OfficeAddressStepProps {
  formValues: OfficeEditFormValues;
}

const OfficeAddressStep: React.FC<OfficeAddressStepProps> = ({ formValues: parentFormValues }) => {
  const officeId = parentFormValues.id;
  const { t } = useTranslate();
  const [addresses, setAddresses] = useState<OfficeAddressRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editAddressId, setEditAddressId] = useState<string | null>(null);
  const [deleteAddressId, setDeleteAddressId] = useState<string | null>(null);

  const fetchAddresses = useCallback(async () => {
    if (!officeId) return;
    setIsLoading(true);
    try {
      const result = await getOfficeAddressesAPI(initialDataWithPagination.pagination, officeId);
      setAddresses(result.data);
    } catch {
      setAddresses([]);
    }
    setIsLoading(false);
  }, [officeId]);

  useEffect(() => {
    if (officeId) fetchAddresses();
  }, [officeId]);

  const handleRowClick = useCallback((addressId: string) => {
    setEditAddressId(addressId);
    setShowEdit(true);
  }, []);

  const handleAddNew = () => {
    setEditAddressId(null);
    setShowEdit(true);
  };

  // Klik na koš ne smije otvoriti i uređivanje retka ispod njega.
  const handleDeleteClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, addressId: string) => {
      event.stopPropagation();
      event.preventDefault();

      setDeleteAddressId(addressId);
    },
    []
  );

  const handleDeleteConfirm = async () => {
    if (!officeId || !deleteAddressId) return;
    await deleteOfficeAddressAPI(t, officeId, deleteAddressId);
    setDeleteAddressId(null);
    fetchAddresses();
  };

  const handleEditSuccess = () => {
    setShowEdit(false);
    setEditAddressId(null);
    fetchAddresses();
  };

  return (
    <>
      <div className="flex items-center justify-center px-4 mt-8 xl:mt-16 min-h-[60vh] xl:min-h-[600px]">
        <div className="w-full max-w-4xl">
          <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">Adrese</h1>
          <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto ">
            Dodajte adrese poslovnice klikom na gumb <strong>"Dodaj adresu"</strong>.
            Nakon unosa možete nastaviti na sljedeći korak.
          </p>

          {/*
            Dok nema nijedne adrese tablica bi bila samo prazno zaglavlje, pa na tom
            mjestu stoji poziv na unos. Uređivanje ide klikom na redak.
          */}
          {!isLoading && addresses.length === 0 ? (
            <div className="max-w-md mx-auto">
              <Button
                variant="primary"
                size="full"
                startIcon="/media/icons/plus.svg"
                children="Dodaj adresu"
                onClick={handleAddNew}
              />
            </div>
          ) : (
            <>
              <div className="flex justify-end mb-3">
                <Button
                  variant="primary"
                  startIcon="/media/icons/plus.svg"
                  children="Dodaj adresu"
                  onClick={handleAddNew}
                />
              </div>

              <TableSearch
                columns={OFFICE_ADDRESS_COLUMNS(handleDeleteClick)}
                data={addresses}
                selectableRows={false}
                onRowClick={handleRowClick}
                isLoading={isLoading}
                disablePagination
              />
            </>
          )}
        </div>
      </div>

      <OfficeAddressEditCard
        officeId={officeId ?? ""}
        addressId={editAddressId ?? ""}
        show={showEdit}
        onSuccess={handleEditSuccess}
        onClose={() => setShowEdit(false)}
      />
      <Dialog
        open={!!deleteAddressId}
        title="Brisanje adrese"
        message="Jeste li sigurni da želite obrisati ovu adresu?"
        confirmText="Obriši"
        cancelText="Odustani"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteAddressId(null)}
      />
    </>
  );
};

export default memo(OfficeAddressStep);
